import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ArrowLeft, Search, X, Loader2 } from 'lucide-react';
import { logFoodAPI } from '../../utils/api';
import FoodResultRow from '../../components/logfood/FoodResultRow';
import RecentlyLoggedList from '../../components/logfood/RecentlyLoggedList';
import FatSecretAttribution from '../../components/logfood/FatSecretAttribution';
import { searchItemToFood } from '../../utils/logFoodMappers';

/**
 * Restaurants tab — step 2: drilldown view for a single chain's menu.
 * Shows: "You usually get" (recent_at_this_chain max 3) + categorized menu.
 */
export default function RestaurantMenuView({
  chain,
  onBack,
  onSelectFood,
  onQuickLog,
}) {
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ categories: [], recent_at_this_chain: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  // Latest-request guard: only the most recent fetch may update state, so a
  // slow response for an older query ("Ca") can't overwrite a newer one.
  const requestSeqRef = useRef(0);

  const fetchMenu = useCallback(function (q) {
    if (!chain || !chain.chain_id) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    const params = { limit: 50 };
    if (q && q.trim()) params.q = q.trim();
    logFoodAPI.getRestaurantMenu(chain.chain_id, params)
      .then(function (res) {
        if (seq !== requestSeqRef.current) return;
        const body = res.data || {};
        setData({
          categories: Array.isArray(body.categories) ? body.categories : [],
          recent_at_this_chain: Array.isArray(body.recent_at_this_chain) ? body.recent_at_this_chain : [],
        });
        setLoading(false);
      })
      .catch(function (err) {
        if (seq !== requestSeqRef.current) return;
        const status = err && err.response && err.response.status;
        setError(status === 503 ? 'disabled' : 'failed');
        setLoading(false);
      });
  }, [chain]);

  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function () { fetchMenu(query); }, 300);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchMenu]);

  if (!chain) return null;

  // Recent chain items must use the SAME canonical normalization as menu rows so
  // FoodConfirm receives per-unit `nutrition` (not a raw item with only `macros`),
  // otherwise the confirm screen defaults every value to 0. Known list macros are
  // preserved here; LogFood.jsx merges richer detail on select without discarding them.
  const selectRecent = function (item) { onSelectFood(searchItemToFood(item)); };
  const quickLogRecent = function (item) { onQuickLog(searchItemToFood(item)); };

  // Recents filter (item 6): empty query -> show all; non-empty -> only rows whose
  // normalized name / brand / restaurant matches; hide the section entirely if none.
  const q = query.trim().toLowerCase();
  const visibleRecents = !q
    ? data.recent_at_this_chain
    : data.recent_at_this_chain.filter(function (it) {
        const hay = [it.name, it.brand, it.restaurant].filter(Boolean).join(' ').toLowerCase();
        return hay.indexOf(q) !== -1;
      });

  return (
    <div className="flex flex-col min-h-screen bg-background" data-testid="restaurant-menu-view">
      <header className="flex items-center gap-2 px-2 pb-3 border-b border-border bg-background sticky top-0 z-10 header-safe-top">
        <button
          type="button"
          onClick={onBack}
          className="size-9 rounded-full hover:bg-accent flex items-center justify-center"
          aria-label="Back"
          data-testid="restaurant-menu-back"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h2 className="flex-1 text-base font-semibold text-foreground truncate" data-testid="restaurant-menu-chain-name">
          {chain.name}
        </h2>
      </header>

      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            placeholder={'Search ' + chain.name + ' menu...'}
            className="w-full pl-9 pr-9 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="restaurant-menu-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={function () { setQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 size-5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
              aria-label="Clear search"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && error === 'disabled' && (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          Restaurant menus activate when FatSecret keys are connected.
        </div>
      )}

      {!loading && error === 'failed' && (
        <div className="px-4 py-12 text-center text-sm text-destructive">
          Failed to load the menu.
        </div>
      )}

      {!loading && !error && visibleRecents.length > 0 && (
        <RecentlyLoggedList
          items={visibleRecents}
          onSelect={selectRecent}
          onQuickLog={quickLogRecent}
          title="Recently Logged"
        />
      )}

      {!loading && !error && data.categories.map(function (cat) {
        return (
          <div key={cat.name} data-testid="restaurant-menu-category">
            <div className="px-4 py-2 mt-2">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {cat.name}
              </h3>
            </div>
            <ul className="divide-y divide-border">
              {(cat.items || []).map(function (item) {
                return (
                  <FoodResultRow
                    key={item.id || item.external_id}
                    item={item}
                    onSelect={function () { onSelectFood(searchItemToFood(item)); }}
                    onQuickLog={function () { onQuickLog(searchItemToFood(item)); }}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}

      {!loading && !error && data.categories.length === 0 && data.recent_at_this_chain.length === 0 && (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          No menu items available.
        </div>
      )}

      <FatSecretAttribution />
    </div>
  );
}
